import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { getAllNestedChildren } from './editor.actions.utils';
import { getEntityData } from '../../../api/entity-data';
import { getEntityDefinition } from '../../../api/entity-defintion';
import { getFormModel } from '../../../api/forms';
import { getTranslations } from '../../../api/translations';
import { trimObjectStrings } from '../../../utils/commonUtils';
import {
  BUTTON_TYPES,
  FORM_KEYS,
  FORM_MODEL_ATTRIBUTES,
  ROOT,
  SS_CONTROL_TYPE,
  STATIC_DATA_SOURCE_LIST,
} from '../../constants/form-manager.constants';
import { INTERACTION_RULES_SUPPORTED_TYPES } from '../../constants/runtime.constants';
import { updateRulesScripts } from '../form-manager.utils';

export const preprocessDataSources = (dataSources) =>
  Object.keys(dataSources).reduce(
    (acc, key) => ({
      ...acc,
      [key]: {
        ...dataSources[key],
        runtimeColumnList:
          dataSources[key].dataSourceType === 6
            ? STATIC_DATA_SOURCE_LIST
            : dataSources[key]?.columnList,
      },
    }),
    {}
  );

export const preprocessRules = (formModel, dataReplacement) => {
  return JSON.parse(updateRulesScripts(JSON.stringify(formModel), dataReplacement));
};

const findTargetControlForInteraction = (parameterType, parameter) => ({
  uniqueIdentifier,
  entityName,
  controlType,
}) =>
  parameterType === 'control'
    ? uniqueIdentifier === parameter
    : entityName === parameter &&
      [SS_CONTROL_TYPE.GRID, SS_CONTROL_TYPE.REPEATING_PANEL].includes(controlType);

export const getInteractionType = (type) => {
  switch (type) {
    case 'ADD_ROW': {
      return FORM_MODEL_ATTRIBUTES.ADD_ROW_RULES;
    }
    case 'CLEAR_TABLE': {
      return FORM_MODEL_ATTRIBUTES.CLEAR_TABLE_RULES;
    }
    case 'REBIND_CANVAS': {
      return FORM_MODEL_ATTRIBUTES.REBIND_CANVAS_RULES;
    }
    default:
      return '';
  }
};

export const updateFormModelWithInteractionRules = (formModel) => {
  const formModelControls = Object.values(formModel);
  const formsWithInteraction = formModelControls.filter(
    ({ controlType, interaction }) => controlType === SS_CONTROL_TYPE.FORM && interaction
  );

  formsWithInteraction.forEach(({ interaction: { rules } }) => {
    const filteredRules = rules.filter(
      ({ actions }) => actions.some(({ type }) => INTERACTION_RULES_SUPPORTED_TYPES.includes(type)),
      []
    );

    filteredRules.forEach(({ actions, Conditions, id: ruleId }) => {
      actions.forEach(({ parameter, parameterType, type, id: actionId, conditionMet }) => {
        const logInfo = {
          actionType: type,
          parameterType,
          targetTable: parameter,
          actionId,
          ruleId,
        };

        if (!INTERACTION_RULES_SUPPORTED_TYPES.includes(type)) {
          return;
        }

        const targetControl = formModelControls.find(
          findTargetControlForInteraction(parameterType, parameter)
        );

        if (!targetControl) {
          return;
        }

        const key = getInteractionType(type);
        targetControl[key] = { Conditions, conditionMet, logInfo };
      });
    });
  });

  return formModel;
};

// linkType '3' is deprecated, change it to '2'
export const updateLinkTypes = (formModel) => {
  for (const id in formModel) {
    if (
      formModel[id].controlType === SS_CONTROL_TYPE.HYPER_LINK &&
      String(formModel[id].linkType) === '3'
    ) {
      formModel[id].linkType = '2';
    }
  }

  return formModel;
};

export const preprocessFilters = (formModel) => {
  for (const id in formModel) {
    if (formModel[id].filter) {
      formModel[id].filter = trimObjectStrings(formModel[id].filter);
    }
  }

  return formModel;
};

/**
 * Checks if ssZoom is standalone control, when it's a child of ssAdvancedTextBox.
 * Such zoom doesn't depend on its ssTextBox sibling.
 */
export const getIsSsZoomStandalone = (formModel, id) => {
  const parentControl = formModel[formModel[id].parent];

  if (parentControl.controlType !== SS_CONTROL_TYPE.ADVANCED_TEXT_BOX) {
    return true;
  }

  const ssZoomIndex = parentControl.childrenNodes.findIndex(
    (childId) => formModel[childId].controlType === SS_CONTROL_TYPE.ZOOM
  );
  const ssTextViewIndex = parentControl.childrenNodes.findIndex(
    (childId) => formModel[childId].controlType === SS_CONTROL_TYPE.TEXT_VIEW
  );

  return ssTextViewIndex === -1 ? false : ssZoomIndex > ssTextViewIndex;
};

export const defineStandaloneZooms = (formModel) => {
  for (const id in formModel) {
    if (formModel[id].controlType !== SS_CONTROL_TYPE.ZOOM) {
      continue;
    }

    if (getIsSsZoomStandalone(formModel, id)) {
      formModel[id].isStandalone = true;
    }
  }

  return formModel;
};

export const addOfflineSaveButton = (formModel) => {
  const controlId = uuidv4();

  const offlineSaveButtonNode = {
    [controlId]: {
      controlType: 'ssButton',
      controlId,
      buttonType: BUTTON_TYPES.SAVE_OFFLINE,
      causesValidation: 'false',
      label: 'Save Offline',
      defaultLabel: 'Save Offline',
      childrenNodes: [],
    },
  };

  const isSaveButtonExist = _.some(formModel, (item) =>
    [BUTTON_TYPES.SAVE_AND_SIGNOFF, BUTTON_TYPES.SAVE_WITHOUT_SIGNOFF, BUTTON_TYPES.OTHER].includes(
      item.buttonType
    )
  );

  if (isSaveButtonExist) {
    formModel = { ...formModel, ...offlineSaveButtonNode };
  }

  return formModel;
};

export const preprocessFormModel = (formModel, formKey) => {
  formModel = updateFormModelWithInteractionRules(formModel);
  formModel = preprocessRules(formModel);
  formModel = updateLinkTypes(formModel);
  formModel = preprocessFilters(formModel);
  formModel = addOfflineSaveButton(formModel);

  if (formKey !== FORM_KEYS.EDITOR) {
    formModel = defineStandaloneZooms(formModel);
  }

  return formModel;
};

export const preprocessFormModelForPreview = (formModel) => {
  const rootElements = Object.keys(formModel).filter((key) => formModel[key].parent === ROOT);

  const root = rootElements[0];
  const panelHeader = formModel[root].childrenNodes[0];

  const childrenIds = getAllNestedChildren({ controlId: panelHeader, formModel });
  const ids = [root, panelHeader, ...childrenIds];

  return _.pick(formModel, ids);
};

export const getFormRules = (formModel) =>
  Object.values(formModel).reduce((acc, control) => {
    if (control.controlType !== SS_CONTROL_TYPE.FORM || !control.update?.rules?.length) {
      return acc;
    }

    const rules = control.update.rules.map((rule) => ({ ...rule, formId: control.controlId }));
    return [...acc, ...rules];
  }, []);

export const setFetchErrorProp = (errorsArray = []) => {
  const modifiedErrorsArray = errorsArray.map((error) => {
    error.hasFetchError = true;
    return error;
  });
  return modifiedErrorsArray;
};

export const getArrayOfPromisesForCreateEntity = ({
  isStepFormDefined,
  formKey,
  formId,
  orgId,
  containerWorkflowKey,
  workflowKey,
  workflowId,
  stepId,
  forEdit,
  entityTypeForDisplay,
  entityType,
  itmId,
  parentWorkflowId,
  entityName,
}) => [
  getFormModel({
    formKey: isStepFormDefined ? '' : formKey,
    formId,
    orgId,
    containerWorkflowKey,
    workflowKey,
    workflowId,
    stepId,
    forEdit,
    itmId,
  }),
  getEntityDefinition(entityTypeForDisplay ?? entityType, undefined, '', itmId || ''),
  getEntityData(
    entityType,
    itmId || '',
    !itmId ? entityType : '',
    workflowId,
    stepId,
    containerWorkflowKey,
    workflowKey,
    undefined,
    parentWorkflowId ?? '',
    entityName
  ),
  getTranslations(
    orgId,
    formKey ? formKey : '',
    containerWorkflowKey,
    workflowKey,
    workflowId,
    stepId,
    entityTypeForDisplay ?? entityType,
    undefined,
    '',
    itmId || '',
    forEdit,
    formId
  ),
];

export const getArrayOfPromisesForCreateCertificationEntity = ({
  formEntityKey,
  formKey,
  itmId,
  stepId,
  isDetail,
  entityType,
  certificationData = true,
  trainerSignOff = false,
}) => [
  getFormModel({ formKey, itmId }),
  getEntityDefinition(entityType, certificationData),
  getEntityData(
    entityType,
    itmId,
    formEntityKey,
    undefined,
    isDetail ? undefined : stepId,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    certificationData,
    trainerSignOff
  ),
  getTranslations(
    undefined,
    formKey,
    undefined,
    undefined,
    undefined,
    stepId,
    entityType,
    certificationData,
    '',
    itmId || ''
  ),
];

export const getArrayOfPromisesForCreateDocumentEntity = ({
  entityType,
  formKey,
  itmId,
  certificationData,
}) => [
  getFormModel({ formKey, itmId }),
  getEntityDefinition(entityType, certificationData),
  getEntityData(
    entityType,
    itmId,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    certificationData
  ),
  getTranslations(
    undefined,
    formKey,
    undefined,
    undefined,
    undefined,
    undefined,
    entityType,
    certificationData,
    '',
    itmId || ''
  ),
];

export const getArrayOfPromisesForCreateFullEntity = ({
  orgId,
  containerWorkflowKey,
  workflowKey,
  workflowId,
  stepId,
  formKey,
  entityType,
  itmId,
  parentWorkflowId,
  entityName,
}) => [
  // step form
  getFormModel({
    formKey: '',
    orgId,
    containerWorkflowKey,
    workflowKey,
    workflowId,
    stepId,
    itmId,
  }),

  // detail form
  getFormModel({
    formKey,
    orgId,
    containerWorkflowKey,
    workflowKey,
    workflowId: null,
    stepId,
    itmId,
  }),

  getEntityDefinition(entityType, undefined, '', itmId),
  getEntityData(
    entityType,
    itmId || '',
    !itmId ? entityType : '',
    workflowId,
    stepId,
    containerWorkflowKey,
    workflowKey,
    undefined,
    parentWorkflowId ?? '',
    entityName
  ),

  // step form
  getTranslations(
    orgId,
    '',
    containerWorkflowKey,
    workflowKey,
    workflowId,
    stepId,
    entityType,
    undefined,
    '',
    itmId || ''
  ),

  // detail form
  getTranslations(
    orgId,
    formKey,
    containerWorkflowKey,
    workflowKey,
    workflowId,
    stepId,
    entityType,
    undefined,
    '',
    itmId || ''
  ),
];

export const getArrayOfPromisesForCreateNewEntity = ({
  formId,
  formKey,
  orgId,
  containerWorkflowKey,
  workflowKey,
  workflowId,
  stepId,
  forEdit,
  itmId,
  parentWorkflowId,
  entityName,
  isCreateForm,
  newFormParams,
}) => [
  getFormModel({
    isCreateForm,
    newFormParams,
  }),
  getEntityDefinition(newFormParams.formEntity, undefined, '', itmId || ''),
  getEntityData(
    newFormParams.formEntity,
    itmId || '',
    newFormParams.formEntity,
    workflowId,
    stepId,
    containerWorkflowKey,
    workflowKey,
    undefined,
    parentWorkflowId ?? '',
    entityName
  ),
  getTranslations(
    orgId,
    formKey ? formKey : '',
    containerWorkflowKey,
    workflowKey,
    workflowId,
    stepId,
    newFormParams.formEntity,
    undefined,
    '',
    itmId || '',
    forEdit,
    formId
  ),
];

export const getArrayOfPromisesForCreateImportEntity = ({
  formId,
  formKey,
  orgId,
  containerWorkflowKey,
  workflowKey,
  workflowId,
  stepId,
  forEdit,
  itmId,
  parentWorkflowId,
  entityName,
  formEntity,
}) => [
  { formDefinition: null },
  getEntityDefinition(formEntity, undefined, '', itmId || ''),
  getEntityData(
    formEntity,
    itmId || '',
    formEntity,
    workflowId,
    stepId,
    containerWorkflowKey,
    workflowKey,
    undefined,
    parentWorkflowId ?? '',
    entityName
  ),
  getTranslations(
    orgId,
    formKey ? formKey : '',
    containerWorkflowKey,
    workflowKey,
    workflowId,
    stepId,
    formEntity,
    undefined,
    '',
    itmId || '',
    forEdit,
    formId
  ),
];

export const mergeFormTranslations = (stepFormTranslations = {}, detailFormTranslations = {}) => {
  const safeStepFormTranslations = {
    Form: stepFormTranslations.Form ?? {},
    Entity: stepFormTranslations.Entity ?? {},
  };
  const safeDetailFormTranslations = {
    Form: detailFormTranslations.Form ?? {},
    Entity: detailFormTranslations.Entity ?? {},
  };

  return {
    Form: {
      ...safeDetailFormTranslations.Form,
      ...safeStepFormTranslations.Form,
    },
    Entity: {
      ...safeDetailFormTranslations.Entity,
      ...safeStepFormTranslations.Entity,
    },
  };
};
